import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';

interface InvoiceItem {
  lotNumber: string;
  sku: string;
  title: string;
  quantity: number;
  highBid: number;
  picked?: boolean;
  storageLocation?: string;
}

interface Invoice {
  invoiceNumber: string;
  auctionId: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  items: InvoiceItem[];
  total: number;
  status: string;
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
}

interface LotWithImages {
  sku: string;
  lotNumber: string;
  images: string[];
}

const PickOrderView: React.FC = () => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lotImages, setLotImages] = useState<{[sku: string]: string[]}>({});
  const [pickedItems, setPickedItems] = useState<Set<string>>(new Set());
  const [storageLocation, setStorageLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [galleryImages, setGalleryImages] = useState<string[] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadInvoiceAndImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  const loadInvoiceAndImages = async () => {
    if (!invoiceNumber) return;

    try {
      // Load invoice
      const invoiceDoc = await getDoc(doc(db, 'invoices', invoiceNumber));
      if (!invoiceDoc.exists()) {
        toast.error('Invoice not found');
        navigate('/picker');
        return;
      }

      const invoiceData = invoiceDoc.data() as Invoice;
      setInvoice(invoiceData);
      setStorageLocation(invoiceData.storageLocation || '');

      // Load images for each lot from the auction
      const auctionDoc = await getDoc(doc(db, 'auctions', invoiceData.auctionId));
      if (auctionDoc.exists()) {
        const auction = auctionDoc.data();
        const images: {[sku: string]: string[]} = {};
        
        invoiceData.items.forEach(item => {
          const lot = auction.lots.find((l: any) => l.sku === item.sku || l.lotNumber === item.lotNumber);
          if (lot && lot.images && lot.images.length > 0) {
            images[item.sku] = lot.images;
          }
        });
        
        setLotImages(images);
      }

      // Initialize picked items if invoice already has them
      if (invoiceData.items.some(item => item.picked)) {
        const picked = new Set(invoiceData.items.filter(item => item.picked).map(item => item.sku));
        setPickedItems(picked);
      }

    } catch (error) {
      console.error('Error loading:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemPicked = (sku: string) => {
    setPickedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
        toast.success('Item unmarked');
      } else {
        newSet.add(sku);
        toast.success('Item picked! ✓');
      }
      return newSet;
    });
  };

  const openGallery = (sku: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't toggle picked status
    const images = lotImages[sku] || [];
    if (images.length > 0) {
      setGalleryImages(images);
      setCurrentImageIndex(0);
    }
  };

  const closeGallery = () => {
    setGalleryImages(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (galleryImages) {
      setCurrentImageIndex((prev) => 
        prev < galleryImages.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (galleryImages) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : galleryImages.length - 1
      );
    }
  };

  const handleSaveProgress = async () => {
    if (!invoice) return;

    setSaving(true);
    try {
      // Update items with picked status
      const updatedItems = invoice.items.map(item => ({
        ...item,
        picked: pickedItems.has(item.sku)
      }));

      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        items: updatedItems,
        storageLocation: storageLocation || undefined,
        updatedAt: new Date().toISOString()
      });

      toast.success('Progress saved!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAllPicked = async () => {
    if (!invoice) return;

    if (pickedItems.size !== invoice.items.length) {
      toast.error('Please pick all items first');
      return;
    }

    if (!storageLocation.trim()) {
      toast.error('Please enter storage location');
      return;
    }

    setSaving(true);
    try {
      const updatedItems = invoice.items.map(item => ({
        ...item,
        picked: true
      }));

      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        items: updatedItems,
        pickStatus: 'picked',
        storageLocation: storageLocation,
        pickedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      toast.success('Order completed and stored!');
      navigate('/picker');
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to complete order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading order...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-7xl mx-auto p-6 text-center">
        <p className="text-gray-600">Invoice not found</p>
      </div>
    );
  }

  const allPicked = pickedItems.size === invoice.items.length;
  const progress = invoice.items.length > 0 ? (pickedItems.size / invoice.items.length) * 100 : 0;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <button
            onClick={() => navigate('/picker')}
            className="text-blue-600 hover:text-blue-800 mb-2"
          >
            ← Back to Picker
          </button>
          <h1 className="text-3xl font-bold">
            Picking Order: {invoice.bidder.lastName}, {invoice.bidder.firstName}
          </h1>
          <p className="text-gray-600">
            Bidder #{invoice.bidderNumber} • {invoice.bidder.phone}
          </p>
          <p className="text-sm text-gray-500 font-mono">{invoice.invoiceNumber}</p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-green-600">
            ${invoice.total.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-2">
          <span className="font-semibold">Picking Progress</span>
          <span className="text-lg font-bold">
            {pickedItems.size} / {invoice.items.length} items
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className={`h-4 rounded-full transition-all duration-300 ${
              allPicked ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Storage Location */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="block font-semibold mb-2">Storage Location:</label>
        <div className="flex gap-3">
          <input
            type="text"
            value={storageLocation}
            onChange={(e) => setStorageLocation(e.target.value)}
            placeholder="e.g., Shelf A-12, Bay 3, Back Wall..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
          />
          <button
            onClick={handleSaveProgress}
            disabled={saving}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Progress'}
          </button>
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {invoice.items.map((item) => {
          const isPicked = pickedItems.has(item.sku);
          const images = lotImages[item.sku] || [];
          
          // Find the _01.jpg image, or fall back to second image if no _01, or first if only one
          let thumbnail = null;
          if (images.length > 0) {
            const image01 = images.find(img => img.includes('_01.jpg') || img.includes('_01.'));
            if (image01) {
              thumbnail = image01;
            } else if (images.length > 1) {
              thumbnail = images[1]; // Use second image (skip barcode)
            } else {
              thumbnail = images[0];
            }
          }

          return (
            <div
              key={item.sku}
              onClick={() => toggleItemPicked(item.sku)}
              className={`relative bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transition-all ${
                isPicked
                  ? 'ring-4 ring-green-500 opacity-75'
                  : 'hover:shadow-xl hover:scale-105'
              }`}
            >
              {/* Picked Overlay */}
              {isPicked && (
                <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center z-10">
                  <div className="bg-green-500 text-white rounded-full p-4">
                    <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Image */}
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt={item.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">No Image</span>
                </div>
              )}

              {/* Item Info */}
              <div className="p-3">
                <div className="font-bold text-sm mb-1">Lot {item.lotNumber}</div>
                <div className="text-xs text-gray-600 mb-1 line-clamp-2">{item.title}</div>
                <div className="flex justify-between items-center text-xs mb-2">
                  <span className="text-gray-500">SKU: {item.sku}</span>
                  <span className="font-bold text-green-600">${item.highBid.toFixed(2)}</span>
                </div>
                {images.length > 1 && (
                  <button
                    onClick={(e) => openGallery(item.sku, e)}
                    className="w-full px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                  >
                    📸 View All {images.length} Images
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Complete Button */}
      {allPicked && (
        <div className="bg-white rounded-lg shadow p-6">
          <button
            onClick={handleMarkAllPicked}
            disabled={saving || !storageLocation.trim()}
            className="w-full px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold text-xl disabled:opacity-50"
          >
            ✓ Complete Order & Store at: {storageLocation || '[Enter Location]'}
          </button>
          {!storageLocation.trim() && (
            <p className="text-red-600 text-sm mt-2 text-center">
              Please enter storage location above before completing
            </p>
          )}
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryImages && galleryImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeGallery}
        >
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-50"
          >
            ×
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ‹
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ›
              </button>
            </>
          )}

          <div
            className="max-w-5xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded"
            />
            {galleryImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded">
                {currentImageIndex + 1} / {galleryImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PickOrderView;